import React from 'react';
import ReactDOMServer from 'react-dom/server';
import KidsCafeProgramAgreement2024 from '../components/KidsCafeProgramAgreement2024';

/**
 * Opens a new browser window with the Kids Cafe program agreement rendered,
 * then auto-triggers the print dialog for PDF download.
 *
 * @param {Object} params
 * @param {string} params.siteName - Site name
 * @param {string} params.submittedAt - Application submission date
 * @param {string} params.nameSign - Name of the person who signed the agreement
 */
const downloadKidsCafeAgreement = ({ siteName, submittedAt, nameSign }) => {
  // Render the agreement component with mock Field props (input/meta)
  const agreementHTML = ReactDOMServer.renderToStaticMarkup(
    <KidsCafeProgramAgreement2024
      submittedAt={submittedAt}
      site={{ name: siteName }}
      input={{ value: true, onChange: () => {} }}
      meta={{ touched: false, error: null }}
    />
  );

  // Fix image paths: convert relative paths to absolute URLs
  const origin = window.location.origin;
  let fixedHTML = agreementHTML.replace(/src="\/static\//g, `src="${origin}/static/`);
  fixedHTML = fixedHTML.replace(/src="\/([^"]*?)"/g, (match, path) => {
    if (path.startsWith('http') || path.startsWith('data:')) return match;
    return `src="${origin}/${path}"`;
  });

  // Copy stylesheets from parent document, excluding @media print rules
  const styleSheets = Array.from(document.styleSheets);
  let cssText = '';
  styleSheets.forEach((sheet) => {
    try {
      if (sheet.cssRules) {
        Array.from(sheet.cssRules).forEach((rule) => {
          if (rule instanceof CSSMediaRule && rule.conditionText && rule.conditionText.includes('print')) {
            return;
          }
          cssText += rule.cssText + '\n';
        });
      }
    } catch (e) {
      if (sheet.href) {
        cssText += `@import url("${sheet.href}");\n`;
      }
    }
  });

  // Signature block
  const signatureHTML = `
    <div style="padding: 30px 41pt; font-family: Arial, sans-serif;">
      <div style="margin-bottom: 16px;">
        <p style="font-weight: bold; margin: 0 0 4px 0;">Agreement Sign</p>
        <div style="border: 1px solid #ddd; border-bottom: 2px solid #ccc; background: #f7f7f7; padding: 10px 14px; max-width: 350px; display: inline-block;">
          ${nameSign || ''}
        </div>
        <span style="margin-left: 20px; font-weight: bold;">I Agree &#10003;</span>
      </div>
    </div>
  `;

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Kids Cafe Program Agreement - ${siteName}</title>
      <style>
        ${cssText}

        .agreement-div.scroll,
        .agreement-div {
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
          border: none !important;
          box-shadow: none !important;
        }

        body {
          background: white;
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }

        .kc-agreement-wrapper {
          padding: 20px;
          max-width: 900px;
          margin: 0 auto;
          background: white;
        }

        @media print {
          body, body * {
            visibility: visible !important;
          }
          .kc-agreement-wrapper {
            padding: 0 !important;
            max-width: 100% !important;
            margin: 0 !important;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
        }
      </style>
    </head>
    <body>
      <div class="kc-agreement-wrapper">
        ${fixedHTML}
        ${signatureHTML}
      </div>
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 500);
        };
      </script>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

export default downloadKidsCafeAgreement;
